import React, { useState } from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonButton,
  IonIcon,
  IonNote,
  IonSpinner,
} from '@ionic/react';
import { addOutline, closeOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { validateName, validateEmail, validatePhone, getValidationError } from '../utils/validation';

interface InlineSupplierFormProps {
  onCancel: () => void;
  onSave: (supplierData: any) => Promise<void>;
}

const InlineSupplierForm: React.FC<InlineSupplierFormProps> = ({ onCancel, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    contactName: '',
    notes: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    sortCode: '',
    iban: '',
    swiftCode: '',
    bankCountry: '',
    accountType: '' as '' | 'private' | 'business',
    paypalEmail: '',
    balance: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required';
    } else if (!validateName(formData.name)) {
      newErrors.name = getValidationError('name', formData.name) || '';
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = getValidationError('email', formData.email) || '';
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = getValidationError('phone', formData.phone) || '';
    }

    if (formData.contactName && !validateName(formData.contactName)) {
      newErrors.contactName = getValidationError('name', formData.contactName) || '';
    }

    if (formData.paypalEmail && !validateEmail(formData.paypalEmail)) {
      newErrors.paypalEmail = 'Invalid PayPal email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      // Clean the data - only include non-empty fields
      const cleanedData: any = {
        name: formData.name.trim(),
      };

      // Only include optional fields if they have values
      if (formData.email?.trim()) cleanedData.email = formData.email.trim();
      if (formData.phone?.trim()) cleanedData.phone = formData.phone.trim();
      if (formData.contactName?.trim()) cleanedData.contactName = formData.contactName.trim();
      if (formData.notes?.trim()) cleanedData.notes = formData.notes.trim();
      if (formData.bankName?.trim()) cleanedData.bankName = formData.bankName.trim();
      if (formData.accountName?.trim()) cleanedData.accountName = formData.accountName.trim();
      if (formData.accountNumber?.trim()) cleanedData.accountNumber = formData.accountNumber.trim();
      if (formData.sortCode?.trim()) cleanedData.sortCode = formData.sortCode.trim();
      if (formData.iban?.trim()) cleanedData.iban = formData.iban.trim();
      if (formData.swiftCode?.trim()) cleanedData.swiftCode = formData.swiftCode.trim();
      if (formData.bankCountry?.trim()) cleanedData.bankCountry = formData.bankCountry.trim();
      if (formData.accountType) cleanedData.accountType = formData.accountType;
      if (formData.paypalEmail?.trim()) cleanedData.paypalEmail = formData.paypalEmail.trim();
      if (formData.balance !== 0) cleanedData.balance = formData.balance;

      await onSave(cleanedData);
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        contactName: '',
        notes: '',
        bankName: '',
        accountName: '',
        accountNumber: '',
        sortCode: '',
        iban: '',
        swiftCode: '',
        bankCountry: '',
        accountType: '',
        paypalEmail: '',
        balance: 0,
      });
      setErrors({});
    } catch (error) {
      console.error('Error saving supplier:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 mt-4 border border-purple-200 dark:border-purple-800">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-md font-semibold text-purple-900 dark:text-purple-100">
          Add New Supplier
        </h4>
        <IonButton
          fill="clear"
          size="small"
          onClick={onCancel}
          disabled={saving}
        >
          <IonIcon icon={closeOutline} />
        </IonButton>
      </div>

      <div className="space-y-3">
        <IonItem
          lines="none"
          className={`bg-white dark:bg-gray-900 rounded-lg ${errors.name ? 'ion-invalid' : ''}`}
        >
          <IonLabel position="stacked" className="text-sm font-medium mb-1">
            Supplier Name <span className="text-red-500">*</span>
          </IonLabel>
          <IonInput
            value={formData.name}
            onIonChange={(e) => {
              setFormData({ ...formData, name: e.detail.value! });
              if (errors.name) {
                setErrors({ ...errors, name: '' });
              }
            }}
            placeholder="e.g., John's Tickets Ltd"
            disabled={saving}
          />
          {errors.name && (
            <IonNote slot="error" className="text-red-500 text-xs mt-1">
              {errors.name}
            </IonNote>
          )}
        </IonItem>

        <IonItem
          lines="none"
          className={`bg-white dark:bg-gray-900 rounded-lg ${errors.contactName ? 'ion-invalid' : ''}`}
        >
          <IonLabel position="stacked" className="text-sm font-medium mb-1">
            Contact Name
          </IonLabel>
          <IonInput
            value={formData.contactName}
            onIonChange={(e) => {
              setFormData({ ...formData, contactName: e.detail.value! });
              if (errors.contactName) {
                setErrors({ ...errors, contactName: '' });
              }
            }}
            placeholder="e.g., John Smith"
            disabled={saving}
          />
          {errors.contactName && (
            <IonNote slot="error" className="text-red-500 text-xs mt-1">
              {errors.contactName}
            </IonNote>
          )}
        </IonItem>

        <IonItem
          lines="none"
          className={`bg-white dark:bg-gray-900 rounded-lg ${errors.email ? 'ion-invalid' : ''}`}
        >
          <IonLabel position="stacked" className="text-sm font-medium mb-1">
            Email
          </IonLabel>
          <IonInput
            type="email"
            value={formData.email}
            onIonChange={(e) => {
              setFormData({ ...formData, email: e.detail.value! });
              if (errors.email) {
                setErrors({ ...errors, email: '' });
              }
            }}
            placeholder="supplier@example.com"
            disabled={saving}
          />
          {errors.email && (
            <IonNote slot="error" className="text-red-500 text-xs mt-1">
              {errors.email}
            </IonNote>
          )}
        </IonItem>

        <IonItem
          lines="none"
          className={`bg-white dark:bg-gray-900 rounded-lg ${errors.phone ? 'ion-invalid' : ''}`}
        >
          <IonLabel position="stacked" className="text-sm font-medium mb-1">
            Phone
          </IonLabel>
          <IonInput
            type="tel"
            value={formData.phone}
            onIonChange={(e) => {
              setFormData({ ...formData, phone: e.detail.value! });
              if (errors.phone) {
                setErrors({ ...errors, phone: '' });
              }
            }}
            placeholder="+44 7000 000000"
            disabled={saving}
          />
          {errors.phone && (
            <IonNote slot="error" className="text-red-500 text-xs mt-1">
              {errors.phone}
            </IonNote>
          )}
        </IonItem>

        <IonItem lines="none" className="bg-white dark:bg-gray-900 rounded-lg">
          <IonLabel position="stacked" className="text-sm font-medium mb-1">
            Notes
          </IonLabel>
          <IonTextarea
            value={formData.notes}
            onIonChange={(e) => setFormData({ ...formData, notes: e.detail.value! })}
            placeholder="Any additional information..."
            rows={3}
            disabled={saving}
          />
        </IonItem>

        <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>
        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Payment Details</h5>

        {/* Bank Details */}
        <div className="grid grid-cols-2 gap-3">
          <IonItem lines="none" className="bg-white dark:bg-gray-900 rounded-lg">
            <IonLabel position="stacked" className="text-sm font-medium mb-1">
              Bank Name
            </IonLabel>
            <IonInput
              value={formData.bankName}
              onIonChange={(e) => setFormData({ ...formData, bankName: e.detail.value! })}
              placeholder="e.g., Barclays"
              disabled={saving}
            />
          </IonItem>

          <IonItem lines="none" className="bg-white dark:bg-gray-900 rounded-lg">
            <IonLabel position="stacked" className="text-sm font-medium mb-1">
              Account Number
            </IonLabel>
            <IonInput
              value={formData.accountNumber}
              onIonChange={(e) => setFormData({ ...formData, accountNumber: e.detail.value! })}
              placeholder="e.g., 12345678"
              disabled={saving}
            />
          </IonItem>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <IonItem lines="none" className="bg-white dark:bg-gray-900 rounded-lg">
            <IonLabel position="stacked" className="text-sm font-medium mb-1">
              IBAN
            </IonLabel>
            <IonInput
              value={formData.iban}
              onIonChange={(e) => setFormData({ ...formData, iban: e.detail.value! })}
              placeholder="e.g., GB33BUKB20201555555555"
              disabled={saving}
            />
          </IonItem>

          <IonItem lines="none" className="bg-white dark:bg-gray-900 rounded-lg">
            <IonLabel position="stacked" className="text-sm font-medium mb-1">
              SWIFT Code
            </IonLabel>
            <IonInput
              value={formData.swiftCode}
              onIonChange={(e) => setFormData({ ...formData, swiftCode: e.detail.value! })}
              placeholder="e.g., BUKBGB22"
              disabled={saving}
            />
          </IonItem>
        </div>

        {/* PayPal */}
        <IonItem
          lines="none"
          className={`bg-white dark:bg-gray-900 rounded-lg ${errors.paypalEmail ? 'ion-invalid' : ''}`}
        >
          <IonLabel position="stacked" className="text-sm font-medium mb-1">
            PayPal Email
          </IonLabel>
          <IonInput
            type="email"
            value={formData.paypalEmail}
            onIonChange={(e) => {
              setFormData({ ...formData, paypalEmail: e.detail.value! });
              if (errors.paypalEmail) {
                setErrors({ ...errors, paypalEmail: '' });
              }
            }}
            placeholder="paypal@example.com"
            disabled={saving}
          />
          {errors.paypalEmail && (
            <IonNote slot="error" className="text-red-500 text-xs mt-1">
              {errors.paypalEmail}
            </IonNote>
          )}
        </IonItem>

        {/* Balance */}
        <IonItem lines="none" className="bg-white dark:bg-gray-900 rounded-lg">
          <IonLabel position="stacked" className="text-sm font-medium mb-1">
            Opening Balance (Positive = They owe you, Negative = You owe them)
          </IonLabel>
          <IonInput
            type="number"
            value={formData.balance}
            onIonChange={(e) => setFormData({ ...formData, balance: parseFloat(e.detail.value!) || 0 })}
            placeholder="0.00"
            disabled={saving}
            step="0.01"
          />
        </IonItem>

        <div className="flex gap-2 mt-4">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={saving || !formData.name.trim()}
            className="flex-1"
            color="primary"
          >
            {saving ? (
              <>
                <IonSpinner name="crescent" className="mr-2" />
                Creating...
              </>
            ) : (
              <>
                <IonIcon icon={addOutline} className="mr-2" />
                Create Supplier
              </>
            )}
          </IonButton>
          <IonButton
            expand="block"
            fill="outline"
            onClick={onCancel}
            disabled={saving}
            color="medium"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </div>
  );
};

export default InlineSupplierForm;